import React, { useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiMoreHorizontal, FiPlus, FiRefreshCw, FiSearch, FiX } from "react-icons/fi";
import { navItems, quickLinks } from "../data/hrmMockData";

const desktopGroups = [
  { label: "Workspace", itemIds: ["dashboard", "projects", "time", "leave", "expenses"] },
  { label: "People", itemIds: ["profile", "myForms", "payroll", "benefits", "performance"] },
  { label: "Company", itemIds: ["events", "ronin", "policies", "assets", "directory", "learning"] },
  { label: "Support", itemIds: ["requests", "myCases"] },
];

const mobilePrimaryIds = ["dashboard", "time", "leave", "payroll"];
const mobilePrimaryLabels = {
  dashboard: "Home",
  leave: "Leave",
  payroll: "Payslips",
  time: "Time",
};

const HrmSidebar = ({
  activeItem,
  canViewEvents = false,
  canViewMyCases = false,
  collapsed,
  formsAvailable = false,
  formsActionRequired = false,
  onAction,
  onNavigate,
  onOpenCommand = () => {},
  onReloadData = () => {},
  onToggleCollapse,
}) => {
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const mobileMoreButtonRef = useRef(null);
  const mobileSheetRef = useRef(null);

  const visibleItems = navItems.filter((item) => {
    if (item.id === "myForms") {
      return formsAvailable || formsActionRequired;
    }
    if (item.id === "events") {
      return canViewEvents;
    }
    return !item.workerOnly || canViewMyCases;
  });

  const visibleItemsById = new Map(visibleItems.map((item) => [item.id, item]));
  const mobilePrimaryItems = mobilePrimaryIds
    .map((itemId) => visibleItemsById.get(itemId))
    .filter(Boolean);
  const mobileMoreItems = visibleItems.filter((item) => !mobilePrimaryIds.includes(item.id));
  const mobileQuickLinks = quickLinks.filter((link) => ["leave", "time", "expense", "payslip"].includes(link.action));
  const mobileMoreActive = mobileMoreItems.some((item) => item.id === activeItem);

  const closeMobileMore = (restoreFocus = false) => {
    setMobileMoreOpen(false);
    if (restoreFocus) {
      mobileMoreButtonRef.current?.focus();
    }
  };

  useEffect(() => {
    if (!mobileMoreOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const desktopMedia = typeof window.matchMedia === "function"
      ? window.matchMedia("(min-width: 761px)")
      : null;
    const focusableSelector = "button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex='-1'])";
    const closeOnDesktop = (event) => {
      if (event.matches) {
        setMobileMoreOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMobileMoreOpen(false);
        mobileMoreButtonRef.current?.focus();
        return;
      }

      if (event.key === "Tab" && mobileSheetRef.current) {
        const focusableElements = Array.from(mobileSheetRef.current.querySelectorAll(focusableSelector));
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (!firstElement || !lastElement) {
          event.preventDefault();
        } else if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    desktopMedia?.addEventListener?.("change", closeOnDesktop);
    mobileSheetRef.current?.querySelector(focusableSelector)?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      desktopMedia?.removeEventListener?.("change", closeOnDesktop);
    };
  }, [mobileMoreOpen]);

  const handleQuickLink = (link) => {
    if (link.action === "profile") {
      onNavigate("profile");
      return;
    }
    onAction(link.action);
  };

  const handleMobileNavigate = (itemId) => {
    setMobileMoreOpen(false);
    onNavigate(itemId);
  };

  const handleMobileQuickLink = (link) => {
    setMobileMoreOpen(false);
    handleQuickLink(link);
  };

  const renderNavItem = (item, className, onClick) => {
    const Icon = item.icon;
    const NavElement = item.href ? "a" : "button";
    const isActive = item.id === activeItem;
    const isMobilePrimary = className.includes("new-hrm-mobile-nav__primary-item");
    const showDot = (item.id === "myForms" && formsActionRequired) || item.id === "events";
    const itemAriaLabel = item.id === "events"
      ? `${item.label}, new event`
      : showDot
        ? `${item.label}, action required`
        : item.label;

    return (
      <NavElement
        key={item.id}
        type={item.href ? undefined : "button"}
        href={item.href}
        target={item.href ? "_blank" : undefined}
        rel={item.href ? "noopener noreferrer" : undefined}
        className={[
          className,
          isActive ? "new-hrm-sidebar__item--active" : "",
          item.highlight ? "new-hrm-sidebar__item--highlight" : "",
          showDot ? "new-hrm-sidebar__item--has-dot" : "",
        ].filter(Boolean).join(" ")}
        aria-label={itemAriaLabel}
        aria-current={isActive ? "page" : undefined}
        title={item.href ? `${item.label} (opens in a new tab)` : item.label}
        onClick={() => item.href ? closeMobileMore(true) : onClick(item.id)}
      >
        <Icon aria-hidden="true" />
        <span>
          {isMobilePrimary ? mobilePrimaryLabels[item.id] || item.label : item.label}
          {item.hint && <small>{className.includes("new-hrm-mobile-nav__item") ? item.shortHint || item.hint : item.hint}</small>}
        </span>
        {showDot && <em className="new-hrm-sidebar__dot" aria-hidden="true" />}
      </NavElement>
    );
  };

  return (
    <>
      <aside className={`new-hrm-sidebar ${collapsed ? "new-hrm-sidebar--collapsed" : ""}`}>
        <div className="new-hrm-sidebar__brand">
          <img
            className="new-hrm-sidebar__logo"
            src="/assets/antrazal-brand.svg"
            alt={collapsed ? "" : "Antrazal"}
          />
          <img
            className="new-hrm-sidebar__logo-mark"
            src="/assets/antrazal-brand-mark.svg"
            alt={collapsed ? "Antrazal" : ""}
          />
          <button
            type="button"
            className="new-hrm-sidebar__collapse"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            onClick={onToggleCollapse}
          >
            <FiChevronLeft aria-hidden="true" />
          </button>
        </div>

        <button
          type="button"
          className="new-hrm-sidebar__search"
          onClick={onOpenCommand}
          aria-label="Search Antrazal"
          title="Search Antrazal"
        >
          <FiSearch aria-hidden="true" />
          <span>Search</span>
          <kbd>Ctrl K</kbd>
        </button>

        <nav
          className="new-hrm-sidebar__nav new-hrm-sidebar__desktop-nav"
          aria-label="HRM navigation"
        >
          {desktopGroups.map((group) => {
            const groupItems = group.itemIds
              .map((itemId) => visibleItemsById.get(itemId))
              .filter(Boolean);

            if (!groupItems.length) {
              return null;
            }

            return (
              <section className="new-hrm-sidebar__group" key={group.label}>
                <h2 className="new-hrm-sidebar__group-label">{group.label}</h2>
                {groupItems.map((item) => renderNavItem(
                  item,
                  "new-hrm-sidebar__item",
                  onNavigate,
                ))}
              </section>
            );
          })}
        </nav>

        <div className="new-hrm-sidebar__footer" aria-hidden="true">
          <strong>Think. Own. Solve.</strong>
          <span>Employee Hub</span>
        </div>

      </aside>

      <nav className="new-hrm-mobile-nav" aria-label="Mobile HRM navigation">
        <div className="new-hrm-mobile-nav__primary">
          {mobilePrimaryItems.map((item) => renderNavItem(
            item,
            "new-hrm-mobile-nav__item new-hrm-mobile-nav__primary-item",
            handleMobileNavigate,
          ))}
          <button
            ref={mobileMoreButtonRef}
            type="button"
            className={[
              "new-hrm-mobile-nav__item",
              "new-hrm-mobile-nav__more",
              mobileMoreActive ? "new-hrm-mobile-nav__item--active" : "",
            ].filter(Boolean).join(" ")}
            aria-label="More navigation options"
            aria-expanded={mobileMoreOpen}
            aria-controls="new-hrm-mobile-navigation-sheet"
            aria-current={mobileMoreActive ? "page" : undefined}
            onClick={() => setMobileMoreOpen(true)}
          >
            <FiMoreHorizontal aria-hidden="true" />
            <span>More</span>
          </button>
        </div>

        {mobileMoreOpen && (
          <>
            <div
              className="new-hrm-mobile-nav__backdrop"
              role="presentation"
              onClick={() => closeMobileMore(true)}
            />
            <section
              ref={mobileSheetRef}
              id="new-hrm-mobile-navigation-sheet"
              className="new-hrm-mobile-nav__sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby="new-hrm-mobile-navigation-title"
            >
              <header className="new-hrm-mobile-nav__sheet-header">
                <h2 id="new-hrm-mobile-navigation-title">More</h2>
                <button
                  type="button"
                  className="new-hrm-mobile-nav__item"
                  aria-label="Close navigation menu"
                  onClick={() => closeMobileMore(true)}
                >
                  <FiX aria-hidden="true" />
                </button>
              </header>
              <div className="new-hrm-mobile-nav__sheet-grid">
                {mobileMoreItems.map((item) => renderNavItem(
                  item,
                  "new-hrm-mobile-nav__item",
                  handleMobileNavigate,
                ))}
              </div>
              <section className="new-hrm-mobile-nav__quick-actions" aria-labelledby="new-hrm-mobile-quick-actions-title">
                <h3 id="new-hrm-mobile-quick-actions-title">Quick actions</h3>
                <div>
                  <button type="button" onClick={() => { closeMobileMore(false); onOpenCommand(); }}>
                    <FiSearch aria-hidden="true" />
                    <span>Search</span>
                  </button>
                  <button type="button" onClick={() => { closeMobileMore(false); onAction("quick"); }}>
                    <FiPlus aria-hidden="true" />
                    <span>Create</span>
                  </button>
                  <button type="button" onClick={() => { closeMobileMore(false); onReloadData(); }}>
                    <FiRefreshCw aria-hidden="true" />
                    <span>Refresh data</span>
                  </button>
                  {mobileQuickLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <button key={link.id} type="button" onClick={() => handleMobileQuickLink(link)}>
                        <Icon aria-hidden="true" />
                        <span>{link.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </section>
          </>
        )}
      </nav>
    </>
  );
};

export default HrmSidebar;
